import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { CheckCircle, Loader, AlertCircle, CreditCard } from 'lucide-react';

// Initialize Stripe with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Secure Payment Form Component
const SecurePaymentForm = ({ 
  selectedPlan, 
  billingAddress, 
  customerInfo,
  onSuccess, 
  onError 
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
    hidePostalCode: true, // We collect this separately
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    try {
      // Create payment method securely on Stripe's servers
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: customerInfo.name,
          email: customerInfo.email,
          address: {
            line1: billingAddress.street,
            city: billingAddress.city,
            state: billingAddress.state,
            postal_code: billingAddress.zipCode,
            country: billingAddress.country || 'US'
          }
        },
      });

      if (pmError) {
        throw new Error(pmError.message);
      }

      console.log('Payment method created successfully:', paymentMethod.id);

      // Now send only the secure payment method ID to your backend
      const securePaymentData = {
        payment_method_id: paymentMethod.id, // ✅ Secure token only
        selected_plan: selectedPlan.id,
        billing_address: billingAddress,
        customer_info: customerInfo
      };

      console.log('Sending secure payment data:', securePaymentData);
      
      // Call your success handler with the secure data
      onSuccess(securePaymentData);

    } catch (err) {
      console.error('Payment processing error:', err);
      setError(err.message);
      onError && onError(err);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">🔒 Secure Payment Processing</h3>
        <p className="text-blue-800 text-sm">
          Your payment information is processed securely by Stripe and never stored on our servers.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Card Information *
          </label>
          <div className="border border-slate-300 rounded-lg p-4 bg-white">
            <CardElement options={cardElementOptions} />
          </div>
          {error && (
            <p className="text-red-500 text-sm mt-2">{error}</p>
          )}
        </div>

        <button
          onClick={handleSubmit}
          disabled={!stripe || processing}
          className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 px-6 rounded-lg font-medium transition-all flex items-center justify-center space-x-2"
        >
          {processing ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              <span>Processing Payment...</span>
            </>
          ) : (
            <>
              <CreditCard className="w-5 h-5" />
              <span>Complete Setup</span>
            </>
          )}
        </button>
      </div>

      <div className="text-center">
        <p className="text-slate-500 text-xs">
          🔒 Powered by Stripe • Your payment information is encrypted and secure
        </p>
      </div>
    </div>
  );
};

// Updated Step 4 for AuthForm that uses secure payment processing
export const SecurePaymentStep = ({ 
  orgData, 
  setOrgData, 
  errors, 
  plans, 
  onSecurePaymentComplete 
}) => {
  const selectedPlan = plans.find(p => p.id === orgData.selectedPlan);

  const handleSecurePaymentSuccess = (securePaymentData) => {
    console.log('Secure payment data received:', securePaymentData);
    
    // Store the secure payment method ID instead of raw card data
    const billingData = {
      selectedPlan: orgData.selectedPlan,
      payment_method_id: securePaymentData.payment_method_id, // ✅ Secure token
      billingAddress: orgData.billingAddress,
      userEmail: orgData.userEmail || '',
      tenantName: orgData.tenantName,
      fullName: orgData.fullName || ''
    };
    
    // Call the completion handler
    onSecurePaymentComplete(billingData);
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Complete Your Setup</h2>
        <p className="text-slate-600">Secure payment processing via Stripe</p>
      </div>

      {selectedPlan && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-semibold text-slate-900">{selectedPlan.name}</h3>
              <p className="text-slate-600 text-sm">{selectedPlan.description}</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-slate-900">
                ${selectedPlan.price}
              </div>
              <div className="text-slate-600 text-sm">
                {selectedPlan.billing}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Billing Address Collection */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900">Billing Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Full Name *
            </label>
            <input
              type="text"
              value={orgData.nameOnCard}
              onChange={(e) => setOrgData(prev => ({ ...prev, nameOnCard: e.target.value }))}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.nameOnCard ? 'border-red-300' : 'border-slate-300'
              }`}
              placeholder="John Doe"
            />
            {errors.nameOnCard && <p className="text-red-500 text-sm mt-1">{errors.nameOnCard}</p>}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Street Address *
            </label>
            <input
              type="text"
              value={orgData.billingAddress.street}
              onChange={(e) => setOrgData(prev => ({ 
                ...prev, 
                billingAddress: { ...prev.billingAddress, street: e.target.value }
              }))}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.street ? 'border-red-300' : 'border-slate-300'
              }`}
              placeholder="123 Main Street"
            />
            {errors.street && <p className="text-red-500 text-sm mt-1">{errors.street}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              City *
            </label>
            <input
              type="text"
              value={orgData.billingAddress.city}
              onChange={(e) => setOrgData(prev => ({ 
                ...prev, 
                billingAddress: { ...prev.billingAddress, city: e.target.value }
              }))}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.city ? 'border-red-300' : 'border-slate-300'
              }`}
              placeholder="New York"
            />
            {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              State *
            </label>
            <input
              type="text"
              value={orgData.billingAddress.state}
              onChange={(e) => setOrgData(prev => ({ 
                ...prev, 
                billingAddress: { ...prev.billingAddress, state: e.target.value }
              }))}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.state ? 'border-red-300' : 'border-slate-300'
              }`}
              placeholder="NY"
            />
            {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Zip Code *
            </label>
            <input
              type="text"
              value={orgData.billingAddress.zipCode}
              onChange={(e) => setOrgData(prev => ({ 
                ...prev, 
                billingAddress: { ...prev.billingAddress, zipCode: e.target.value }
              }))}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.zipCode ? 'border-red-300' : 'border-slate-300'
              }`}
              placeholder="10001"
            />
            {errors.zipCode && <p className="text-red-500 text-sm mt-1">{errors.zipCode}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Country
            </label>
            <select
              value={orgData.billingAddress.country}
              onChange={(e) => setOrgData(prev => ({ 
                ...prev, 
                billingAddress: { ...prev.billingAddress, country: e.target.value }
              }))}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="UK">United Kingdom</option>
              <option value="AU">Australia</option>
            </select>
          </div>
        </div>
      </div>

      {/* Secure Payment Form */}
      <Elements stripe={stripePromise}>
        <SecurePaymentForm
          selectedPlan={selectedPlan}
          billingAddress={orgData.billingAddress}
          customerInfo={{
            name: orgData.nameOnCard,
            email: orgData.userEmail || ''
          }}
          onSuccess={handleSecurePaymentSuccess}
          onError={handlePaymentError}
        />
      </Elements>
    </div>
  );
};

export default SecurePaymentStep;